import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { cn } from '@/lib/utils';
import { SeasonCardAccent } from '@/components/season/SeasonCardAccent';
import { SeasonButtonAccent, SeasonCorner, SeasonGlyph } from '@/components/season/SeasonDetails';

/**
 * Детали раскладки «Карта» (19.10.2026): карта подписки, круги быстрых действий,
 * плитка сводки, переключатель подписок, группа строк, кнопки. Цвета, радиусы и
 * шрифты берутся из темы (index.css: `--t-*`, `--r-*`, `--card-*`). Цвет партнёра
 * заливает площадь только на карте, всё вокруг нейтральное.
 */

const press = 'transition-transform duration-100 active:scale-[0.97]';

/**
 * Карта подписки въезжает только при первом показе за сессию. Вкладки монтируют экран
 * заново, и раньше карта выезжала на каждом возврате на главную: на частом действии
 * анимация быстро надоедает и отнимает кадры у отрисовки экрана.
 */
let cardEntered = false;
export function useFirstEnter(): boolean {
  const [first] = useState(() => !cardEntered);
  useEffect(() => {
    cardEntered = true;
  }, []);
  return first;
}

/* ---------- Карта подписки ---------- */

export type CardTone = 'ok' | 'warn' | 'muted';

const TONE_DOT: Record<CardTone, string> = {
  ok: 'bg-[var(--t-ok)]',
  warn: 'bg-[var(--t-warn)]',
  muted: 'bg-current opacity-60',
};

/** Знак бота: логотип партнёра или первая буква названия. */
export function BotMark({ className }: { className?: string }) {
  const { botTitle, botLogoUrl } = api.getAuth();
  const [broken, setBroken] = useState(false);
  const letter = (botTitle ?? 'VPN').trim().charAt(0).toUpperCase() || 'V';
  return (
    <span
      aria-hidden
      className={cn(
        'tl-mark grid size-7 shrink-0 place-items-center overflow-hidden rounded-[9px] bg-[var(--card-chip)] text-[13px] font-bold shadow-[inset_0_0_0_1px_var(--card-chip-line)]',
        className,
      )}
    >
      {botLogoUrl && !broken ? (
        <img src={botLogoUrl} alt="" className="size-full object-cover" onError={() => setBroken(true)} />
      ) : (
        letter
      )}
    </span>
  );
}

export function SubCard({
  status,
  tone = 'ok',
  kicker,
  title,
  meta,
  aside,
  mini,
  frozen,
  onClick,
  label,
  progress,
}: {
  /** Статус в чипе справа сверху. Нет статуса (карта заказа): чипа нет. */
  status?: string;
  tone?: CardTone;
  /** Подпись над названием тарифа («Тариф», «Срок»). */
  kicker?: string;
  title: ReactNode;
  meta?: ReactNode;
  /** Правый нижний угол: ∞ или число дней. */
  aside?: ReactNode;
  mini?: boolean;
  /** Пауза: карта гаснет, как выключенная. */
  frozen?: boolean;
  onClick?: () => void;
  label?: string;
  /** Доля оставшегося срока, 0..1. Нет у бессрочной: тогда нет ни линейки, ни кольца. */
  progress?: number;
}) {
  const botTitle = api.getAuth().botTitle;
  const enter = useFirstEnter();
  const body = (
    <>
      {/* Зерно поверх градиента: без него заливка выглядит пластиком. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 mix-blend-overlay"
        style={{ opacity: 'var(--card-grain)', backgroundImage: GRAIN }}
      />
      <SeasonCardAccent />
      {/* Метки обреза по углам: видны только в «Схеме». */}
      {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
        <span key={c} aria-hidden className="tl-crop hidden" data-pos={c} />
      ))}
      <span className="tl-layer relative flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2.5 text-[15px] font-semibold">
          <BotMark />
          <span className="truncate">{botTitle ?? 'VPN'}</span>
        </span>
        {status && (
          <span className="tl-chip inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-full bg-[var(--card-chip)] px-2.5 text-[12px] font-medium shadow-[inset_0_0_0_1px_var(--card-chip-line)]">
            <span className={cn('tl-dot size-1.5 rounded-full', TONE_DOT[tone])} />
            {status}
          </span>
        )}
      </span>
      <span className="tl-layer relative block">
      <span className="flex items-end justify-between gap-3">
        <span className="min-w-0">
          {kicker && <span className="tl-kicker mb-1 block text-[12px] opacity-70">{kicker}</span>}
          <span
            className={cn(
              'block font-semibold leading-none tracking-[-0.03em] [overflow-wrap:anywhere]',
              mini ? 'line-clamp-1 text-[24px]' : 'line-clamp-2 text-[30px] leading-[1.05]',
            )}
          >
            {title}
          </span>
          {meta && <span className="tl-card-meta mt-2 block truncate text-[13px] opacity-80">{meta}</span>}
        </span>
        {aside && (
          <span className="tl-card-aside shrink-0 text-right leading-none" data-ring={progress !== undefined ? '' : undefined}>
            {aside}
          </span>
        )}
      </span>
      {/* Линейка срока («Схема»): пройденное цветом партнёра. */}
      {progress !== undefined && (
        <span aria-hidden className="tl-ruler hidden">
          <i style={{ width: `${Math.round((1 - clamp01(progress)) * 100)}%` }} />
        </span>
      )}
      </span>
    </>
  );
  const cls = cn(
    'tl-card relative flex w-full flex-col justify-between overflow-hidden rounded-[var(--r-subcard)] p-5 text-left text-[var(--card-fg)]',
    'shadow-[inset_0_0_0_1px_var(--card-line),var(--card-shadow)]',
    mini ? 'min-h-[148px]' : 'aspect-[1.586]',
    enter && 'enter-soft',
    frozen && 'grayscale-[0.7]',
    onClick && press,
  );
  const style = {
    background: 'var(--card-bg)',
    ...(progress !== undefined && { '--p': `${Math.round(clamp01(progress) * 100)}%` }),
  } as CSSProperties;
  return onClick ? (
    <button
      type="button"
      aria-label={label}
      className={cls}
      style={style}
      onClick={() => {
        haptic('light');
        onClick();
      }}
    >
      {body}
    </button>
  ) : (
    <div className={cls} style={style}>
      {body}
    </div>
  );
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/** Крупная цифра в углу карты: число дней с подписью или ∞. */
export function CardAside({ value, unit }: { value: string; unit?: string }) {
  return (
    <>
      <span className={cn('block tabular font-semibold tracking-[-0.03em]', value === '∞' ? 'text-[44px] font-light' : 'text-[34px]')}>
        {value}
      </span>
      {unit && <span className="mt-1 block text-[12px] opacity-75">{unit}</span>}
    </>
  );
}

/* ---------- Быстрые действия ---------- */

export interface QuickAction {
  key: string;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  /** Главное действие: круг цветом партнёра. */
  primary?: boolean;
}

export function QuickActions({ items }: { items: QuickAction[] }) {
  return (
    <div className="tl-actions grid gap-2" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((a) => (
        <button
          key={a.key}
          type="button"
          data-primary={a.primary ? '' : undefined}
          onClick={() => {
            haptic('light');
            a.onClick();
          }}
          className={cn('tl-act group relative flex min-w-0 flex-col items-center gap-2 text-[12px] font-medium', a.primary ? 'text-foreground' : 'text-[var(--t-muted)]')}
        >
          <span
            className={cn(
              'tl-act-ic relative grid size-[58px] place-items-center rounded-full [&>svg:not(.tl-season)]:size-[22px] [&>svg:not(.tl-season)]:[stroke-width:1.8]',
              press,
              a.primary
                ? 'bg-primary text-[var(--brand-cta-foreground)]'
                : 'border border-[var(--t-line)] bg-[var(--t-surface)] text-foreground shadow-[var(--t-shadow)]',
            )}
          >
            {a.icon}
            {a.primary && <SeasonButtonAccent at="icon" />}
          </span>
          {a.primary && <SeasonButtonAccent at="button" />}
          <span className="w-full truncate text-center">{a.label}</span>
        </button>
      ))}
    </div>
  );
}

/* ---------- Плитка сводки ---------- */

export function StatTile({
  icon,
  label,
  value,
  unit,
  hint,
  children,
  onClick,
  className,
}: {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  unit?: ReactNode;
  hint?: ReactNode;
  /** Низ плитки: полоса, флаги. */
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const body = (
    <>
      <SeasonCorner />
      <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-[var(--t-muted)] [&_svg]:size-4 [&_svg]:shrink-0">
        {icon && <span className="tl-tile-ic contents">{icon}</span>}
        <span className="truncate">{label}</span>
      </span>
      <span className="mt-5 block">
        <span className="block text-[28px] font-semibold leading-none tracking-[-0.03em] tabular">
          {value}
          {unit && <span className="ml-1 text-[15px] font-medium tracking-normal text-[var(--t-muted)]">{unit}</span>}
        </span>
        {hint && <span className="mt-1.5 block truncate text-[12px] text-[var(--t-muted)]">{hint}</span>}
        {children}
      </span>
    </>
  );
  const cls = cn(
    'tl-tile relative overflow-hidden flex min-h-[132px] flex-col justify-between rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] p-4 text-left shadow-[var(--t-shadow)]',
    onClick && press,
    className,
  );
  return onClick ? (
    <button
      type="button"
      className={cls}
      onClick={() => {
        haptic('light');
        onClick();
      }}
    >
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function MiniBar({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cn('tl-bar mt-3 h-1 w-full overflow-hidden rounded-full bg-[var(--t-fill-strong)]', className)}>
      <div className="h-full rounded-full bg-foreground" style={{ width: `${Math.max(2, Math.min(100, value * 100))}%` }} />
    </div>
  );
}

/** Полоса из делений: остаток пакетом, как индикатор заряда. */
export function SegmentBar({ value, segments = 25 }: { value: number; segments?: number }) {
  const on = Math.round(Math.max(0, Math.min(1, value)) * segments);
  return (
    <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${segments}, minmax(0, 1fr))` }} aria-hidden>
      {Array.from({ length: segments }).map((_, i) => (
        <span key={i} className={cn('h-[22px] rounded-[3px]', i < on ? 'bg-foreground' : 'bg-[var(--t-fill-strong)]')} />
      ))}
    </div>
  );
}

/* ---------- Переключатель подписок ---------- */

export function SubSwitcher<T extends { id: string; label: string }>({
  items,
  value,
  onChange,
}: {
  items: T[];
  value: string;
  onChange: (id: string) => void;
}) {
  if (items.length < 2) return null;
  return (
    // py-1: прокрутка по x режет и по y, без запаса хит-область вкладок обрезалась бы.
    // Отрицательных вертикальных отступов нет намеренно: родитель держит ритм через
    // space-y, а -my-1 перебивал нижний отступ, и карточка тарифа вставала вплотную.
    <div className="relative z-[1] -mx-4 overflow-x-auto px-4 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="tl-seg flex w-max gap-1.5" role="tablist">
        {items.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === value}
            onClick={() => {
              haptic('light');
              onChange(s.id);
            }}
            className={cn(
              // Хит-область до 44px псевдоэлементом: сама таблетка 36px, псевдоэлемент
              // считается от внутреннего края рамки, отсюда 5px, а не 4. truncate на
              // подписи, а не на кнопке: overflow:hidden у кнопки срезал бы псевдоэлемент.
              'tl-seg-item relative h-9 max-w-[220px] shrink-0 rounded-[10px] border px-3.5 text-[13px] font-medium transition-colors duration-150 after:absolute after:inset-x-0 after:-inset-y-[5px] after:content-[""]',
              s.id === value
                ? 'border-[var(--t-fill-strong)] bg-[var(--t-surface-2)] text-foreground'
                : 'border-[var(--t-line)] text-[var(--t-muted)]',
            )}
          >
            <span className="block truncate">{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Группа строк ---------- */

/** Группа строк с подписью сверху, как секции настроек. */
export function Group({ title, children, className }: { title?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={className}>
      {title && <h2 className="tl-sect mb-2 px-1 text-[13px] font-medium text-[var(--t-muted)]">{title}</h2>}
      <div className="tl-group relative divide-y divide-[var(--t-line)] overflow-hidden rounded-[var(--r-card)] border border-[var(--t-line)] bg-[var(--t-surface)] shadow-[var(--t-shadow)]">
        <SeasonCorner compact />
        {children}
      </div>
    </section>
  );
}

export function Row({
  icon,
  title,
  hint,
  value,
  right,
  onClick,
  danger,
  disabled,
}: {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  value?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  const body = (
    <>
      {icon && (
        <span
          className={cn(
            'tl-row-ic grid size-9 shrink-0 place-items-center rounded-[var(--r-sm)] bg-[var(--t-surface-2)] [&_svg]:size-[18px]',
            danger ? 'text-[var(--t-danger)]' : 'text-foreground',
          )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[15px] font-medium leading-snug', danger && 'text-[var(--t-danger)]')}>{title}</span>
        {hint && <span className="mt-0.5 block text-[13px] leading-snug text-[var(--t-muted)]">{hint}</span>}
      </span>
      {value !== undefined && <span className="shrink-0 text-[15px] text-[var(--t-muted)] tabular">{value}</span>}
      {right ?? (onClick && <ChevronRight className="size-[18px] shrink-0 text-[var(--t-faint)]" />)}
    </>
  );
  const cls = 'tl-row flex w-full min-h-[62px] items-center gap-3.5 px-4 py-3 text-left';
  return onClick ? (
    <button type="button" onClick={onClick} disabled={disabled} className={cn(cls, 'active:bg-[var(--t-fill)] disabled:opacity-50')}>
      {body}
    </button>
  ) : (
    <div className={cn(cls, disabled && 'opacity-50')}>{body}</div>
  );
}

export function ScreenTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <header className="tl-title mb-1 flex items-center justify-between gap-3 px-1 pt-3">
      <h1 className="tl-h flex items-center gap-2 text-[32px] font-semibold leading-tight tracking-[-0.035em] text-foreground">
        <span className="min-w-0">{children}</span>
        <SeasonGlyph />
      </h1>
      {right}
    </header>
  );
}

/** Заголовок раздела внутри экрана: «Сводка», «История» и ссылка справа. */
export function SectionTitle({ children, action, onAction }: { children: ReactNode; action?: string; onAction?: () => void }) {
  return (
    <div className="tl-sect-row mb-3 flex items-baseline justify-between px-1">
      <h2 className="tl-sect text-[20px] font-semibold tracking-[-0.02em]">{children}</h2>
      {action && onAction && (
        <button type="button" onClick={onAction} className="-my-2 min-h-11 px-1 text-[14px] font-medium text-[var(--t-muted)] active:opacity-60">
          {action}
        </button>
      )}
    </div>
  );
}

/* ---------- Кнопки ---------- */

type BtnVariant = 'primary' | 'strong' | 'ghost';

const BTN: Record<BtnVariant, string> = {
  primary: 'calm-cta',
  // «Белая» в тёмной схеме и чёрная в светлой: вторая по силе после цвета партнёра.
  strong: 'bg-foreground text-background',
  ghost: 'tl-btn-ghost bg-[var(--t-surface-2)] text-foreground',
};

export function Btn({
  variant = 'primary',
  icon,
  children,
  onClick,
  disabled,
  className,
  label,
}: {
  variant?: BtnVariant;
  icon?: ReactNode;
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        haptic('light');
        onClick?.();
      }}
      className={cn(
        'flex h-[52px] min-w-0 items-center justify-center gap-2 rounded-[var(--r-btn)] px-4 text-[16px] font-semibold disabled:opacity-50 [&_svg]:size-[18px] [&_svg]:shrink-0',
        press,
        BTN[variant],
        className,
      )}
    >
      {icon}
      {children && <span className="truncate">{children}</span>}
    </button>
  );
}

/* ---------- Число, которое докручивается ---------- */

/**
 * Число докручивается от нуля один раз, при первом показе экрана. Повторные
 * обновления (рефетч) меняют его сразу: бегущая цифра на каждом возврате на вкладку
 * быстро надоедает. Под «уменьшить движение» сразу итог.
 */
/** Уже показанные докрутки: на возврате на вкладку экран монтируется заново, и
 *  `useRef` сбрасывался, так что баланс снова бежал от нуля на каждом визите. */
const countedOnce = new Set<string>();

export function CountUp({ value, format, id = 'default' }: { value: number; format: (n: number) => string; id?: string }) {
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const seen = countedOnce.has(id);
  // Старт с нуля сразу в первом кадре: с `useState(value)` экран сначала показывал
  // итог, а потом цифра падала в ноль и докручивалась заново.
  const [shown, setShown] = useState(() => (reduce || seen || value <= 0 ? value : 0));
  const done = useRef(seen);
  useEffect(() => {
    if (done.current || value <= 0 || reduce) {
      done.current = true;
      countedOnce.add(id);
      setShown(value);
      return;
    }
    const start = performance.now();
    const dur = 650;
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(value * eased);
      if (k < 1) raf = requestAnimationFrame(tick);
      else {
        done.current = true;
        countedOnce.add(id);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, reduce]);
  return <>{format(shown)}</>;
}
